import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom"
import { AuthProvider, useAuth } from "./auth/AuthProvider"
import ProtectedRoute from "./components/ProtectedRoute"
import AdminRoute from "./components/AdminRoute"

import Login from "./auth/Login"
import Register from "./auth/Register"
import Dashboard from "./pages/dashboard/Dashboard"
import Projects from "./pages/projects/Projects"
import ProjectView from "./pages/projects/ProjectView"
import SiteAudit from "./pages/seo/SiteAudit"
import RankTracker from "./pages/seo/RankTracker"
import Keywords from "./pages/seo/Keywords"
import KeywordGap from "./pages/seo/KeywordGap"
import Backlinks from "./pages/seo/Backlinks"
import LinkDiscovery from "./pages/seo/LinkDiscovery"
import Competitors from "./pages/seo/Competitors"
import KeywordCluster from "./pages/seo/KeywordCluster"
import Crawler from "./pages/Crawler"
import BacklinkGraph from "./pages/BacklinkGraph"
import AISEO from "./pages/AISEO"
import AIContent from "./pages/AIContent"
import Autopilot from "./pages/Autopilot"
import Reports from "./pages/Reports"
import AdminDashboard from "./pages/admin/AdminDashboard"
import Users from "./pages/admin/Users"
import Plans from "./pages/admin/Plans"
import Billing from "./pages/admin/Billing"
import Settings from "./pages/admin/Settings"
import "./App.css"

function SidebarLink({ to, children, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? "active" : "")}>
      {children}
    </NavLink>
  )
}

function SidebarSection({ title, items, open = false }) {
  return (
    <details className="nav-group" open={open}>
      <summary>{title}</summary>
      {items.map((item) => (
        <SidebarLink key={item.to} to={item.to} end={item.end}>
          <span className="nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </SidebarLink>
      ))}
    </details>
  )
}

function AppShell() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const isAdmin = ["admin", "superadmin"].includes(user?.role)
  const isSuperAdmin = user?.role === "superadmin"
  const defaultPath = isSuperAdmin ? "/admin/dashboard" : "/dashboard"

  const platformItems = [
    { to: "/admin/dashboard", label: "Control Center", icon: "HQ" },
    { to: "/admin/settings", label: "App & API Settings", icon: "API" },
    { to: "/admin/plans", label: "Plans & Limits", icon: "PL" },
    { to: "/admin/billing", label: "Billing Providers", icon: "BI" },
    { to: "/admin/users", label: "Users & Roles", icon: "UR" }
  ]

  const adminItems = [
    { to: "/admin/dashboard", label: "Admin Overview", icon: "AD" },
    { to: "/admin/users", label: "Users", icon: "UR" },
    { to: "/admin/plans", label: "Plans", icon: "PL" },
    { to: "/admin/billing", label: "Billing", icon: "BI" }
  ]

  const workspaceItems = [
    { to: "/dashboard", label: "SEO Dashboard", icon: "OV" },
    { to: "/projects", label: "Projects", icon: "PR" },
    { to: "/reports", label: "Reports", icon: "RP" }
  ]

  const competitiveItems = [
    { to: "/audit", label: "Domain Overview", icon: "DO" },
    { to: "/competitors", label: "Competitive Research", icon: "CR" },
    { to: "/keyword-gap", label: "Keyword Gap", icon: "KG" }
  ]

  const keywordItems = [
    { to: "/keywords", label: "Keyword Magic", icon: "KM" },
    { to: "/rank", label: "Position Tracking", icon: "PT" },
    { to: "/cluster", label: "Keyword Clusters", icon: "KC" }
  ]

  const linkItems = [
    { to: "/backlinks", label: "Backlink Analytics", icon: "BA" },
    { to: "/links", label: "Link Building Tool", icon: "LB" },
    { to: "/backlink-graph", label: "Backlink Graph", icon: "BG" }
  ]

  const contentItems = [
    { to: "/ai-seo", label: "On Page SEO Checker", icon: "OP" },
    { to: "/content", label: "AI Content Template", icon: "AI" },
    { to: "/autopilot", label: "SEO Autopilot", icon: "AU" },
    { to: "/crawler", label: "Technical Crawler", icon: "TC" }
  ]

  function search(e) {
    e.preventDefault()
    const value = new FormData(e.currentTarget).get("q")
    if (value) navigate("/audit")
  }

  return (
    <div className="app-frame">
      <header className="topbar">
        <button className="brand top-brand" onClick={() => navigate(defaultPath)}>
          <span className="brand-mark">M</span>
          <span>
            <strong>Master SEO Engine</strong>
            <small>{isSuperAdmin ? "Super Admin" : isAdmin ? "Admin" : "Workspace"}</small>
          </span>
        </button>
        <form className="top-search" onSubmit={search}>
          <input name="q" placeholder="Search a domain, keyword or report" />
          <button>Search</button>
        </form>
        <div className="top-actions">
          <NavLink className="button secondary" to="/audit">New audit</NavLink>
          <span className="role-chip">{user?.role || "client"}</span>
          <button className="logout top-logout" onClick={logout}>Logout</button>
        </div>
      </header>

      <div className="layout">
        <aside className="sidebar">
          {isSuperAdmin && <SidebarSection title="Super Admin HQ" items={platformItems} open />}
          {!isSuperAdmin && isAdmin && <SidebarSection title="Admin" items={adminItems} open />}

          <SidebarSection title="SEO Workspace" items={workspaceItems} open />
          <SidebarSection title="Competitive Research" items={competitiveItems} />
          <SidebarSection title="Keyword Research" items={keywordItems} />
          <SidebarSection title="Link Building" items={linkItems} />
          <SidebarSection title="Content & Technical SEO" items={contentItems} />
        </aside>

        <main className="main">
          <Routes>
          <Route path="/" element={<Navigate to={defaultPath} replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectView />} />
          <Route path="/audit" element={<SiteAudit />} />
          <Route path="/rank" element={<RankTracker />} />
          <Route path="/keywords" element={<Keywords />} />
          <Route path="/keyword-gap" element={<KeywordGap />} />
          <Route path="/backlinks" element={<Backlinks />} />
          <Route path="/links" element={<LinkDiscovery />} />
          <Route path="/competitors" element={<Competitors />} />
          <Route path="/cluster" element={<KeywordCluster />} />
          <Route path="/crawler" element={<Crawler />} />
          <Route path="/backlink-graph" element={<BacklinkGraph />} />
          <Route path="/ai-seo" element={<AISEO />} />
          <Route path="/content" element={<AIContent />} />
          <Route path="/autopilot" element={<Autopilot />} />
          <Route path="/reports" element={<Reports />} />
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <Users />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/plans"
            element={
              <AdminRoute>
                <Plans />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <AdminRoute>
                <Billing />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <Settings />
              </AdminRoute>
            }
          />
          </Routes>
        </main>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}
